import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Calculator,
  Building2,
  Users,
  CreditCard,
  LogOut,
  ChevronLeft,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Rocket,
  Landmark,
  UserRound,
  Link2,
} from 'lucide-react';
import { LOGOUT } from '../constants/testIds';
import { toast } from 'sonner';

export default function Sidebar({ onLogout }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [simuladorOpen, setSimuladorOpen] = useState(
    location.pathname.startsWith('/simulador') || location.pathname === '/calculadora'
  );

  const handleLogout = async () => {
    try {
      await logout();
      if (onLogout) onLogout();
      navigate('/login');
      toast.success('Logout realizado');
    } catch {
      navigate('/login');
    }
  };

  const roleLabel = { admin: 'Administrador', corretor: 'Corretor', gerente: 'Gerente' };
  const planBadge = { free: 'Free', pro: 'Pro', enterprise: 'Enterprise' };
  const planColor = {
    free: 'bg-stone-100 text-stone-600',
    pro: 'bg-emerald-100 text-emerald-700',
    enterprise: 'bg-amber-100 text-amber-700',
  };

  const isSimuladorActive = location.pathname.startsWith('/simulador') || location.pathname === '/calculadora';

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-5 border-b border-stone-200 ${collapsed ? 'justify-center' : 'justify-between'}`}>
        {!collapsed && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#064E3B] flex items-center justify-center shrink-0">
              <Building2 size={16} className="text-white" />
            </div>
            <span className="font-bold text-stone-900 text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
              TesIQ
            </span>
          </div>
        )}
        {collapsed && (
          <div className="w-8 h-8 rounded-md bg-[#064E3B] flex items-center justify-center">
            <Building2 size={16} className="text-white" />
          </div>
        )}
        <button
          onClick={() => setCollapsed((v) => !v)}
          className="hidden lg:flex items-center justify-center w-6 h-6 rounded text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors"
        >
          <ChevronLeft size={16} className={`transition-transform ${collapsed ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">

        {/* Dashboard */}
        <NavLink
          to="/"
          end
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) =>
            `sidebar-link ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-2' : ''}`
          }
        >
          <LayoutDashboard size={18} />
          {!collapsed && <span>Dashboard</span>}
        </NavLink>

        {/* Simulador (com submenu) */}
        <div>
          <button
            onClick={() => {
              if (!collapsed) setSimuladorOpen((v) => !v);
              else navigate('/calculadora');
            }}
            className={`sidebar-link w-full ${isSimuladorActive ? 'active' : ''} ${collapsed ? 'justify-center px-2' : 'justify-between'}`}
          >
            <div className="flex items-center gap-2">
              <Calculator size={18} />
              {!collapsed && <span>Simulador</span>}
            </div>
            {!collapsed && (
              <ChevronDown
                size={14}
                className={`text-stone-400 transition-transform ${simuladorOpen ? 'rotate-180' : ''}`}
              />
            )}
          </button>

          {/* Submenu */}
          {!collapsed && simuladorOpen && (
            <div className="ml-4 mt-0.5 space-y-0.5 border-l-2 border-stone-200 pl-3">
              <NavLink
                to="/calculadora"
                end
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `sidebar-link text-sm py-1.5 ${isActive && location.pathname === '/calculadora' && !location.search.includes('tipo=lancamento') && !location.search.includes('tipo=pre') ? 'active' : ''}`
                }
              >
                <Rocket size={14} className="shrink-0" />
                <span>Lançamento</span>
              </NavLink>
              <NavLink
                to="/calculadora?tipo=pre-lancamento"
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `sidebar-link text-sm py-1.5 ${location.search.includes('tipo=pre-lancamento') ? 'active' : ''}`
                }
              >
                <Rocket size={14} className="shrink-0 text-amber-600" />
                <span>Pré-lançamento</span>
              </NavLink>
              <NavLink
                to="/simulador/financiamento"
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `sidebar-link text-sm py-1.5 ${isActive ? 'active' : ''}`
                }
              >
                <Landmark size={14} className="shrink-0" />
                <span>Financiamento</span>
              </NavLink>
            </div>
          )}
        </div>

        {/* Clientes */}
        <NavLink
          to="/clientes"
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) =>
            `sidebar-link ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-2' : ''}`
          }
        >
          <Users size={18} />
          {!collapsed && <span>Clientes</span>}
        </NavLink>

        {/* Produtos */}
        <NavLink
          to="/imoveis"
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) =>
            `sidebar-link ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-2' : ''}`
          }
        >
          <Building2 size={18} />
          {!collapsed && <span>Produtos</span>}
        </NavLink>

        {/* CRM */}
        <NavLink
          to="/crm"
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) =>
            `sidebar-link ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-2' : ''}`
          }
        >
          <Link2 size={18} />
          {!collapsed && <span>CRM</span>}
        </NavLink>

        {/* Planos */}
        <NavLink
          to="/planos"
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) =>
            `sidebar-link ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-2' : ''}`
          }
        >
          <CreditCard size={18} />
          {!collapsed && <span>Planos</span>}
        </NavLink>

        {/* Perfil */}
        <NavLink
          to="/perfil"
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) =>
            `sidebar-link ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-2' : ''}`
          }
        >
          <UserRound size={18} />
          {!collapsed && <span>Perfil</span>}
        </NavLink>

      </nav>

      {/* User + Logout */}
      <div className="border-t border-stone-200 px-3 py-3">
        {!collapsed && user && (
          <div className="flex items-center gap-2.5 px-2 py-2 mb-1">
            <div className="w-8 h-8 rounded-full bg-[#064E3B] flex items-center justify-center shrink-0">
              <span className="text-white text-xs font-bold">
                {(user.name || 'U').charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-stone-800 truncate">{user.name}</p>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-stone-500">{roleLabel[user.role] || user.role}</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${planColor[user.plano] || planColor.free}`}>
                  {planBadge[user.plano] || 'Free'}
                </span>
              </div>
            </div>
          </div>
        )}
        <button
          data-testid={LOGOUT.button}
          onClick={handleLogout}
          className={`sidebar-link w-full text-rose-600 hover:bg-rose-50 hover:text-rose-700 ${collapsed ? 'justify-center' : ''}`}
        >
          <LogOut size={18} />
          {!collapsed && <span>Sair</span>}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 w-10 h-10 bg-white border border-stone-200 rounded-lg flex items-center justify-center shadow-sm"
        onClick={() => setMobileOpen((v) => !v)}
      >
        {mobileOpen ? <X size={18} /> : <Menu size={18} />}
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/30 z-30"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <div
        className={`lg:hidden fixed left-0 top-0 bottom-0 w-64 bg-[#F4F4F1] border-r border-stone-200 z-40 transition-transform duration-200 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent />
      </div>

      {/* Desktop sidebar */}
      <div
        className={`hidden lg:flex flex-col bg-[#F4F4F1] border-r border-stone-200 shrink-0 transition-all duration-200 ${
          collapsed ? 'w-[72px]' : 'w-[260px]'
        }`}
        style={{ minHeight: '100vh' }}
      >
        <SidebarContent />
      </div>
    </>
  );
}
